import React, { useState } from 'react';
import { Shield, CheckCircle, AlertTriangle, DollarSign, Calculator, FileText, Search, RefreshCw } from 'lucide-react';

const PBSEligibility = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  // const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(false);

  // Mock PBS Eligibility data
  const pbsData = {
    safetyNet: {
      currentYear: 2025,
      threshold: 156.30,
      spent: 89.50,
      remaining: 66.80,
      percentage: 57.3,
      nextReset: '2026-01-01'
    },
    residents: [
      {
        id: 'RES001',
        name: 'Margaret Johnson',
        medicare: '1234 56789 0',
        safetyNetStatus: 'Active',
        currentSpending: 45.30,
        threshold: 156.30,
        remaining: 111.00,
        percentage: 29.0,
        medications: [
          {
            name: 'Metformin 500mg',
            pbsCode: 'PBS-001',
            cost: 6.60,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-20'
          },
          {
            name: 'Lisinopril 10mg',
            pbsCode: 'PBS-002',
            cost: 8.20,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-18'
          },
          {
            name: 'Atorvastatin 20mg',
            pbsCode: 'PBS-003',
            cost: 12.50,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-22'
          }
        ]
      },
      {
        id: 'RES002',
        name: 'Robert Smith',
        medicare: '2345 67890 1',
        safetyNetStatus: 'Not Active',
        currentSpending: 23.40,
        threshold: 156.30,
        remaining: 132.90,
        percentage: 15.0,
        medications: [
          {
            name: 'Aspirin 100mg',
            pbsCode: 'PBS-004',
            cost: 4.20,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-15'
          },
          {
            name: 'Warfarin 5mg',
            pbsCode: 'PBS-005',
            cost: 19.20,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-23'
          }
        ]
      },
      {
        id: 'RES003',
        name: 'Mary Williams',
        medicare: '3456 78901 2',
        safetyNetStatus: 'Active',
        currentSpending: 78.90,
        threshold: 156.30,
        remaining: 77.40,
        percentage: 50.5,
        medications: [
          {
            name: 'Insulin Glargine 100U/mL',
            pbsCode: 'PBS-006',
            cost: 25.00,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-21'
          },
          {
            name: 'Metformin 500mg',
            pbsCode: 'PBS-001',
            cost: 6.60,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-19'
          },
          {
            name: 'Gliclazide 80mg',
            pbsCode: 'PBS-007',
            cost: 15.30,
            concession: true,
            safetyNet: true,
            lastDispensed: '2025-01-20'
          }
        ]
      }
    ],
    medications: [
      {
        name: 'Metformin 500mg',
        pbsCode: 'PBS-001',
        concessionPrice: 6.60,
        generalPrice: 12.40,
        safetyNetPrice: 6.60,
        authorityRequired: false,
        quantity: 100,
        repeats: 5
      },
      {
        name: 'Lisinopril 10mg',
        pbsCode: 'PBS-002',
        concessionPrice: 8.20,
        generalPrice: 15.80,
        safetyNetPrice: 8.20,
        authorityRequired: false,
        quantity: 30,
        repeats: 5
      },
      {
        name: 'Atorvastatin 20mg',
        pbsCode: 'PBS-003',
        concessionPrice: 12.50,
        generalPrice: 24.90,
        safetyNetPrice: 12.50,
        authorityRequired: false,
        quantity: 30,
        repeats: 5
      },
      {
        name: 'Warfarin 5mg',
        pbsCode: 'PBS-005',
        concessionPrice: 19.20,
        generalPrice: 38.40,
        safetyNetPrice: 19.20,
        authorityRequired: false,
        quantity: 28,
        repeats: 5
      },
      {
        name: 'Insulin Glargine 100U/mL',
        pbsCode: 'PBS-006',
        concessionPrice: 25.00,
        generalPrice: 50.00,
        safetyNetPrice: 25.00,
        authorityRequired: true,
        quantity: 1,
        repeats: 5
      }
    ]
  };

  const handleCheckEligibility = (residentId) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      // setSelectedResident(residentId);
    }, 1000);
  };

  const getSafetyNetColor = (percentage) => {
    if (percentage >= 80) return 'text-red-600';
    if (percentage >= 60) return 'text-orange-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getSafetyNetBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-100';
    if (percentage >= 60) return 'bg-orange-100';
    if (percentage >= 40) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const filteredResidents = pbsData.residents.filter(resident =>
    resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resident.medicare.includes(searchQuery)
  );

  const totalSpending = pbsData.residents.reduce((sum, resident) => sum + resident.currentSpending, 0);
  const averageSpending = totalSpending / pbsData.residents.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              PBS Eligibility & Safety Net
              <Shield className="w-8 h-8 text-green-200" />
            </h1>
            <p className="mt-2 opacity-90">
              Track PBS eligibility, Safety Net status, and medication costs for all residents
            </p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setLoading(true)}
              disabled={loading}
              className="bg-white text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Syncing...' : 'Sync PBS'}
            </button>
            <button 
              onClick={onClose}
              className="text-white hover:text-green-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Safety Net Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-600" />
          Safety Net Overview - {pbsData.safetyNet.currentYear}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-green-600" />
              <div>
                <h4 className="font-semibold text-green-800">Threshold</h4>
                <p className="text-2xl font-bold text-green-600">${pbsData.safetyNet.threshold}</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <div>
                <h4 className="font-semibold text-blue-800">Spent</h4>
                <p className="text-2xl font-bold text-blue-600">${pbsData.safetyNet.spent}</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-purple-600" />
              <div>
                <h4 className="font-semibold text-purple-800">Remaining</h4>
                <p className="text-2xl font-bold text-purple-600">${pbsData.safetyNet.remaining}</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <div>
                <h4 className="font-semibold text-orange-800">Progress</h4>
                <p className="text-2xl font-bold text-orange-600">{pbsData.safetyNet.percentage}%</p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-green-500 to-orange-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${pbsData.safetyNet.percentage}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Next reset: {pbsData.safetyNet.nextReset}
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">${totalSpending.toFixed(2)}</h3>
              <p className="text-sm text-gray-600">Total Spending</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calculator className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">${averageSpending.toFixed(2)}</h3>
              <p className="text-sm text-gray-600">Average per Resident</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{pbsData.residents.length}</h3>
              <p className="text-sm text-gray-600">Residents Tracked</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search residents by name or Medicare number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Residents List */}
        <div className="space-y-4">
          {filteredResidents.map((resident) => (
            <div key={resident.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <div>
                      <h4 className="font-semibold text-lg text-gray-800">{resident.name}</h4>
                      <p className="text-sm text-gray-600">Medicare: {resident.medicare}</p>
                    </div>
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      resident.safetyNetStatus === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {resident.safetyNetStatus}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Spending</p>
                      <p className="text-lg font-bold text-blue-600">${resident.currentSpending}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Threshold</p>
                      <p className="text-lg font-bold text-gray-600">${resident.threshold}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Remaining</p>
                      <p className="text-lg font-bold text-green-600">${resident.remaining}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Progress</p>
                      <p className={`text-lg font-bold ${getSafetyNetColor(resident.percentage)}`}>
                        {resident.percentage}%
                      </p>
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${getSafetyNetBgColor(resident.percentage)}`}
                      style={{ width: `${resident.percentage}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Medications: {resident.medications.length}</span>
                    <span>Last updated: {resident.medications[0]?.lastDispensed || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleCheckEligibility(resident.id)}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Check Eligibility
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Medication Database */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-600" />
          PBS Medication Database
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Medication</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">PBS Code</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Concession Price</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">General Price</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Safety Net Price</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Authority Required</th>
              </tr>
            </thead>
            <tbody>
              {pbsData.medications.map((med) => (
                <tr key={med.pbsCode} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-800">{med.name}</td>
                  <td className="py-3 px-4 text-gray-600 font-mono">{med.pbsCode}</td>
                  <td className="py-3 px-4 text-green-600 font-semibold">${med.concessionPrice}</td>
                  <td className="py-3 px-4 text-gray-600">${med.generalPrice}</td>
                  <td className="py-3 px-4 text-blue-600 font-semibold">${med.safetyNetPrice}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      med.authorityRequired 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {med.authorityRequired ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PBSEligibility;
